import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Rocket, Zap, Building2, ArrowLeft } from 'lucide-react';

const phases = [
  {
    icon: CheckCircle2,
    badge: 'Today',
    badgeVariant: 'default' as const,
    title: 'Demo — What You Just Saw',
    desc: 'A complete walkthrough of the Pull Over Sales experience using sample Land Rover Waterloo data.',
    items: [
      'Manager dashboard with live floor view, KPIs, and stall alerts',
      'Rep dashboard optimized for mobile use on the lot',
      'Deal detail pages with activity timeline and next-step prompts',
      'Quick log for walkarounds, test drives, and follow-ups',
      'Daily / weekly / monthly leaderboard with CSI scoring',
      'Customer feedback (CSI) capture — green / yellow / red',
      'Branded landing page and role-based login',
    ],
  },
  {
    icon: Rocket,
    badge: '2-Week Pilot',
    badgeVariant: 'default' as const,
    title: 'Next Step — Live Pilot at Land Rover Waterloo',
    desc: 'Stand up a real, persistent instance for the Waterloo floor. Everything that gets logged stays.',
    items: [
      'Real authentication — invite Jonny G + the rest of the floor',
      'Deals, activities, and CSI feedback persist to a real database',
      'Manager (Jeff / GM) sees live activity from every rep in real time',
      'Daily summary email at end of shift',
      'Multi-tenant scoping — only LR Waterloo data visible',
      '2-week measurement window: demos logged, follow-up rate, CSI lift, deals closed',
    ],
  },
  {
    icon: Zap,
    badge: 'Phase 2',
    badgeVariant: 'secondary' as const,
    title: 'Automation Layer',
    desc: 'Once the floor is using it daily, turn on the automations that make it pay for itself.',
    items: [
      'CRM bridge (DealerSocket / VinSolutions / OneEighty) — sync deals both ways',
      'SMS + email follow-up sequences triggered by deal stage',
      'Automated CSI request after delivery',
      'Push notifications for stall alerts and hot-deal updates',
      'Offline mode for the lot (PWA) — log without signal, sync when back online',
      'Appointment confirmations and no-show recovery',
    ],
  },
  {
    icon: Building2,
    badge: 'Phase 3',
    badgeVariant: 'secondary' as const,
    title: 'Group Rollout',
    desc: 'If results hold, expand beyond Waterloo across the dealer group.',
    items: [
      'Multi-store rollout across the JLR / dealer group',
      'Cross-store leaderboard and benchmarking',
      'Manager coaching tools — flag reps trending down before month-end',
      'Advanced analytics: conversion by source, time-to-close, demo→sold ratio',
      'GM / ownership dashboard rolling up all stores',
    ],
  },
];

export default function Roadmap() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-6 -ml-3">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        </Link>

        <div className="mb-10">
          <Badge variant="outline" className="mb-4">Pilot Roadmap · Land Rover Waterloo</Badge>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            What's Next
          </h1>
          <p className="text-muted-foreground text-lg">
            From today's demo to a live pilot to a full group rollout — here's the path.
          </p>
        </div>

        <div className="space-y-5">
          {phases.map((p) => (
            <Card key={p.badge}>
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Badge variant={p.badgeVariant} className="mb-2">{p.badge}</Badge>
                    <CardTitle className="text-xl">{p.title}</CardTitle>
                    <CardDescription className="mt-1.5">{p.desc}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 pl-14">
                  {p.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      <span className="text-foreground/90">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 p-6 rounded-lg border border-primary/20 bg-primary/5 text-center">
          <h2 className="font-semibold text-lg mb-2">Ready to start the pilot?</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Two weeks. Real data. Real reps. Real results — then we decide together.
          </p>
          <Link to="/login">
            <Button size="lg">Open the Demo</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}